import React, { useState } from 'react';
import { ChevronDown, Shield, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/utils';

function RiskMethodology() {
  const [expanded, setExpanded] = useState(false);

  const riskLevels = [
    { level: 'Critical', range: '81-100', variant: 'destructive', Icon: AlertTriangle, action: 'Immediate action' },
    { level: 'High', range: '51-80', variant: 'warning', Icon: AlertTriangle, action: 'Review required' },
    { level: 'Medium', range: '21-50', variant: 'default', Icon: Info, action: 'Monitor closely' },
    { level: 'Low', range: '0-20', variant: 'success', Icon: CheckCircle2, action: 'Normal activity' },
  ];

  const piiScores = [
    { type: 'CVV/Security Codes', score: 40 },
    { type: 'Bank Cards', score: 35 },
    { type: 'Passport/PINFL', score: 30 },
    { type: 'Account Numbers', score: 25 },
    { type: 'Emails/Phones', score: 15 },
  ];

  return (
    <Card>
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4 text-primary" />
          Risk Methodology
        </CardTitle>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setExpanded(!expanded)}
          aria-label="Toggle details"
        >
          <ChevronDown
            className={cn('transition-transform', expanded && 'rotate-180')}
          />
        </Button>
      </CardHeader>
      {expanded && (
        <CardContent>
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2">Severity Levels</h4>
            <div className="flex flex-col gap-2">
              {riskLevels.map((level) => {
                const Icon = level.Icon;
                return (
                  <div key={level.level} className="flex items-center gap-2">
                    <Badge variant={level.variant} className="min-w-[80px] justify-center">
                      <Icon className="h-3 w-3" />
                      {level.level}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {level.range} points - {level.action}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <Separator className="my-4" />

          <h4 className="text-sm font-semibold text-foreground mb-2">PII Risk Scores</h4>
          <Table>
            <TableBody>
              {piiScores.map((item) => (
                <TableRow key={item.type}>
                  <TableCell className="text-sm">{item.type}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline">+{item.score}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="mt-4 rounded-md border border-border bg-muted/30 p-3">
            <p className="text-xs text-muted-foreground">
              Based on NIST 800-53, PCI DSS, and banking security standards
            </p>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default RiskMethodology;
